"use client";

import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface PopupItem {
  ID: number;
  Title: string;
  URL: string;
  ImageUpload: any;
  StartDate: string;
  EndDate: string;
  IsActive: boolean;
}

interface PopupBannerProps {
  data: PopupItem[];
}

export const PopupBanner: React.FC<PopupBannerProps> = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    if (data && data?.length > 0 && !hasShown) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setHasShown(true);
      }, 2000); // 5 seconds delay

      return () => clearTimeout(timer);
    }
  }, [data, hasShown]);

  if (!data || data?.length === 0) return null;

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop/Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Popup Container */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl z-10"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-50 p-2 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full text-white transition-all border border-white/30"
              aria-label="Close popup"
            >
              <X size={24} />
            </button>

            <div className="relative aspect-[4/5] max-h-[70vh]">
              {data.length === 1 ? (
                <a
                  href={data[0].URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full group"
                >
                  <img
                    src={data[0].ImageUpload}
                    alt={data[0].Title}
                    className="w-full h-full object-cover"
                    // aspectRatio="none"
                    // objectFit="cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                </a>
              ) : (
                <Swiper
                  modules={[Autoplay, Navigation, Pagination]}
                  autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                  }}
                  loop={true}
                  navigation={true}
                  className="w-full h-full popupSwiper"
                >
                  {data.map((item) => (
                    <SwiperSlide key={item.ID}>
                      <a
                        href={item.URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full h-full group"
                      >
                        <img
                          src={item.ImageUpload}
                          alt={item.Title}
                          className="w-full h-full object-cover"
                          // aspectRatio="none"
                          // objectFit="cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      </a>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
